import { type ReactElement, type ReactNode } from 'react'
import { Badge } from 'app/components/ui/badge'
import type { GoldenExtraction, GoldenPerson } from './mockData'

// Read-only view of a golden extraction organized by the 5 pipeline stages, so you can
// see exactly what each processing stage is expected to produce. Mirrors the eval
// scorer's stage grouping (validation is run but not scored).

function StageCard({
  index,
  title,
  note,
  children,
}: {
  index: number
  title: string
  note?: string
  children: ReactNode
}): ReactElement {
  return (
    <div className="rounded-lg border border-default bg-surface">
      <div className="flex items-center gap-2 border-b border-subtle px-3 py-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-muted text-[10px] font-semibold text-subtle">
          {index}
        </span>
        <span className="text-xs font-semibold text-default">{title}</span>
        {note && <span className="ml-auto text-[10px] uppercase text-minimal">{note}</span>}
      </div>
      <div className="p-3">{children}</div>
    </div>
  )
}

function KeyValue({ label, value }: { label: string; value?: string }): ReactElement {
  return (
    <div>
      <div className="text-[11px] uppercase text-minimal">{label}</div>
      <div className="text-xs font-medium text-default">{value || `—`}</div>
    </div>
  )
}

function PersonName({ person }: { person: GoldenPerson }): ReactElement {
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-strong">{person.name || `Unknown`}</span>
      {person.isCaller && <span className="rounded bg-surface-muted px-1 text-[10px] text-subtle">caller</span>}
    </span>
  )
}

/** A person's secondary fields for one stage, e.g. "son · 5551234567", or nothing. */
function Details({ values }: { values: (string | undefined)[] }): ReactElement | null {
  const shown = values.filter(Boolean)
  return shown.length ? <div className="mt-0.5 text-[11px] text-subtle">{shown.join(` · `)}</div> : null
}

function appointmentSummary(person: GoldenPerson): string {
  if (person.appointmentScheduled) {
    return `Scheduled ${person.appointmentDate ?? ``} ${person.appointmentTime ?? ``}`.trim()
  }
  if (person.appointmentRequested) return `Requested, not scheduled`
  return `No appointment`
}

export function GoldenStageView({ golden }: { golden: GoldenExtraction }): ReactElement {
  const people = golden.people

  return (
    <div className="space-y-3">
      <StageCard index={1} title="Overview">
        <div className="grid grid-cols-3 gap-3">
          <KeyValue label="Answerer" value={golden.answerer} />
          <KeyValue label="Channel" value={golden.channel} />
          <KeyValue label="Call type" value={golden.callType} />
        </div>
      </StageCard>

      <StageCard index={2} title="People" note={`${people.length} ${people.length === 1 ? `person` : `people`}`}>
        <div className="space-y-2">
          {people.map((person, i) => (
            <div key={i}>
              <div className="flex items-center justify-between">
                <PersonName person={person} />
                <span className="text-xs text-subtle">{person.patientType}</span>
              </div>
              <Details
                values={[
                  person.relationship,
                  person.phone,
                  person.email,
                  person.birthDate && `born ${person.birthDate}`,
                ]}
              />
            </div>
          ))}
        </div>
      </StageCard>

      <StageCard index={3} title="Appointment">
        <div className="space-y-2">
          {people.map((person, i) => (
            <div key={i}>
              <div className="flex items-center justify-between gap-3">
                <PersonName person={person} />
                <span className="text-right text-xs text-subtle">{appointmentSummary(person)}</span>
              </div>
              <Details values={[person.treatment, person.serviceRequested, person.reasonForNotScheduling]} />
            </div>
          ))}
        </div>
      </StageCard>

      <StageCard index={4} title="Outcome">
        <div className="space-y-2">
          {people.map((person, i) => (
            <div key={i}>
              <div className="flex items-start justify-between gap-3">
                <PersonName person={person} />
                <div className="flex flex-wrap justify-end gap-1">
                  {person.outcomes.length > 0 ? (
                    person.outcomes.map((outcome) => <Badge key={outcome} color="gray" label={outcome} />)
                  ) : (
                    <span className="text-xs text-minimal">—</span>
                  )}
                </div>
              </div>
              <Details
                values={[
                  person.insuranceProvider && `insurance: ${person.insuranceProvider}`,
                  person.referral && `referral: ${person.referral}`,
                ]}
              />
            </div>
          ))}
        </div>
      </StageCard>

      <StageCard index={5} title="Validation" note="not scored">
        <p className="text-xs text-minimal">
          Summary and follow-up action items: generated by the pipeline but not scored.
        </p>
      </StageCard>
    </div>
  )
}
